import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
  Button,
  IconButton,
  Card,
  Chip,
  Alert
} from '@mui/material';
import Iconify from '../minimal/iconify/Iconify';
import { useLocales } from '../../locales';

export const DayScheduleForm = ({ schedule, onChange, title }) => {
  const { translate } = useLocales();

  const formatTimeDisplay = (time) => {
    if (!time) return '';
    const hour = time.substring(0, 2);
    const min = time.substring(2, 4);
    return `${hour}:${min}`;
  };

  const handleTimeChange = (field, value) => {
    // Remove any colons and ensure 4 digits
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 4) {
      onChange({ ...schedule, [field]: cleanValue });
    }
  };

  const handleAddBreakTime = () => {
    onChange({
      ...schedule,
      breakTimes: [
        ...(schedule.breakTimes || []),
        { startTime: "1200", endTime: "1300", label: translate('event_settings.lunch_break') }
      ]
    });
  };

  const handleRemoveBreakTime = (index) => {
    const newBreakTimes = [...schedule.breakTimes];
    newBreakTimes.splice(index, 1);
    onChange({ ...schedule, breakTimes: newBreakTimes });
  };

  const handleBreakTimeChange = (index, field, value) => {
    const newBreakTimes = [...schedule.breakTimes];
    if (field === 'label') {
      newBreakTimes[index][field] = value;
    } else {
      const cleanValue = value.replace(/[^0-9]/g, '');
      if (cleanValue.length <= 4) {
        newBreakTimes[index][field] = cleanValue;
      }
    }
    onChange({ ...schedule, breakTimes: newBreakTimes });
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!schedule.enabled || !schedule.startTime || !schedule.endTime) {
      return { totalSlots: 0, totalHours: 0, totalCapacity: 0 };
    }

    const startHour = parseInt(schedule.startTime.substring(0, 2));
    const startMin = parseInt(schedule.startTime.substring(2, 4));
    const endHour = parseInt(schedule.endTime.substring(0, 2));
    const endMin = parseInt(schedule.endTime.substring(2, 4));

    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    const totalHours = totalMinutes / 60;

    // Calculate break time
    let breakMinutes = 0;
    schedule.breakTimes?.forEach(breakTime => {
      const breakStart = parseInt(breakTime.startTime.substring(0, 2)) * 60 +
                         parseInt(breakTime.startTime.substring(2, 4));
      const breakEnd = parseInt(breakTime.endTime.substring(0, 2)) * 60 +
                       parseInt(breakTime.endTime.substring(2, 4));
      breakMinutes += (breakEnd - breakStart);
    });

    const activeMinutes = totalMinutes - breakMinutes;
    const totalSlots = Math.floor(activeMinutes / schedule.slotDuration);
    const totalCapacity = totalSlots * schedule.slotCapacity;

    return { totalSlots, totalHours: totalHours.toFixed(1), totalCapacity };
  };

  const stats = calculateStats();

  return (
    <Stack spacing={3}>
      <Typography variant="h6">{title}</Typography>

      <FormControlLabel
        control={
          <Switch
            checked={schedule.enabled}
            onChange={(e) => onChange({ ...schedule, enabled: e.target.checked })}
          />
        }
        label={translate('event_settings.enable_this_day')}
      />

      {schedule.enabled && (
        <>
          {/* Operating Hours */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={translate('event_settings.opening_time')}
                value={formatTimeDisplay(schedule.startTime)}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                placeholder="09:00"
                helperText={translate('event_settings.time_format_help')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={translate('event_settings.closing_time')}
                value={formatTimeDisplay(schedule.endTime)}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                placeholder="17:00"
                helperText={translate('event_settings.time_format_help')}
              />
            </Grid>
          </Grid>

          {/* Slot Configuration */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={translate('event_settings.slot_duration_minutes')}
                value={schedule.slotDuration}
                onChange={(e) => onChange({
                  ...schedule,
                  slotDuration: parseInt(e.target.value) || 30
                })}
                InputProps={{ inputProps: { min: 15, max: 480, step: 15 } }}
                helperText={translate('event_settings.duration_help')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={translate('event_settings.capacity_per_slot')}
                value={schedule.slotCapacity}
                onChange={(e) => onChange({
                  ...schedule,
                  slotCapacity: parseInt(e.target.value) || 1
                })}
                InputProps={{ inputProps: { min: 1, max: 1000 } }}
                helperText={translate('event_settings.capacity_help')}
              />
            </Grid>
          </Grid>

          {/* Break Times */}
          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {translate('event_settings.break_times')}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={handleAddBreakTime}
                  variant="outlined"
                >
                  {translate('event_settings.add_break')}
                </Button>
              </Stack>

              {schedule.breakTimes?.length > 0 ? (
                schedule.breakTimes.map((breakTime, index) => (
                  <Stack key={index} direction="row" spacing={2} alignItems="center">
                    <TextField
                      size="small"
                      label={translate('event_settings.break_start')}
                      value={formatTimeDisplay(breakTime.startTime)}
                      onChange={(e) => handleBreakTimeChange(index, 'startTime', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    <Typography>-</Typography>
                    <TextField
                      size="small"
                      label={translate('event_settings.break_end')}
                      value={formatTimeDisplay(breakTime.endTime)}
                      onChange={(e) => handleBreakTimeChange(index, 'endTime', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    <TextField
                      size="small"
                      label={translate('event_settings.break_label')}
                      value={breakTime.label}
                      onChange={(e) => handleBreakTimeChange(index, 'label', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveBreakTime(index)}
                      size="small"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                ))
              ) : (
                <Alert severity="info">
                  {translate('event_settings.no_breaks')}
                </Alert>
              )}
            </Stack>
          </Card>

          {/* Preview Statistics */}
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.lighter' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {translate('event_settings.preview')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.operating_hours')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatTimeDisplay(schedule.startTime)} - {formatTimeDisplay(schedule.endTime)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.total_slots')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.totalSlots} {translate('event_settings.slots')}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.daily_capacity')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.totalCapacity} {translate('event_settings.bookings')}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </>
      )}
    </Stack>
  );
};